import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { LevelAsset } from '@services/assets-data.service';
import { Rule, RulesDataService } from '@services/rules-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { RuleEvent } from '@services/events-data.service';
import { AccountsService, IAccountUser } from '@services/accounts.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, ActionsDataService, IActionType } from '@services/actions-data.service';

/** EditRuleDialogData  */
export interface createActionDialogData {
	assetItem: LevelAsset;
	event?: RuleEvent;
	isAttachedtoEvent: boolean;
	editing?: boolean;
	action: any;
}

@Component({
	selector: 'app-create-action',
	templateUrl: './create-action.component.html',
	styleUrls: ['./create-action.component.scss']
})
export class CreateActionComponent implements OnInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected AccountUID */
	public selectedAccountUID: string;

	/** Selected Organisation ID */
	public selectedOrganisationUID: string;

	/** Form Group */
	dialogForm: FormGroup;

	/** asset */
	public asset: LevelAsset;

	/** attachedToEvent */
	public attachedToEvent: boolean = true;

	public actionTypeFields: Object = { text: "Description", value: "ActionTypeID" };

	public accountUserFields: Object = { text: "Name", value: "Sub" };

	/** actionTypes - values correspond to value in db table */
	public actionTypes: IActionType[] = [];

	/** accountUsers */
	public accountUsers: IAccountUser[] = [];

	/** editing */
	public editing: boolean = false;

	/** action */
	public action: Action;

	/** today */
	public today: Date = new Date();

	/** ruleFields */
	public ruleFields: Object = { text: 'RuleDescription', value: 'RuleID' };

	/** Rules */
	public Rules: Rule[] = [];

	/** mode */
	public mode: string;

	/** allSelected */
	public allSelected: boolean = false;

	/** numberSelected */
	public numberSelected: number = 0;

	/** currentUser */
	public currentUser: WebAppUser;

	/**
	 * 
	 * @param fb 
	 * @param dialogRef 
	 * @param globalFunctions 
	 * @param siteSubscriptions 
	 * @param rulesDataService 
	 * @param authenticationService 
	 * @param accountsService 
	 * @param dialogData 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<CreateActionComponent>,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		private rulesDataService: RulesDataService,
		private authenticationService: AuthenticationService,
		private accountsService: AccountsService,
		private actionsDataService: ActionsDataService,
		@Inject(MAT_DIALOG_DATA) public dialogData: createActionDialogData,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.actionsDataService.getActionTypes$().pipe(takeUntil(this.finalise), distinctUntilChanged()),
		]).pipe(
			filter(([selectedAccountGUID, selectedOrganisationUID, currentUser, ActionTypes]) =>
				!!selectedAccountGUID && !!selectedOrganisationUID && !!currentUser && !!ActionTypes
			)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID, currentUser, ActionTypes]) => {
				this.actionTypes = ActionTypes;
				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;
				this.getAccountUsers(this.selectedAccountUID);
				this.mode = 'Box';
				this.currentUser = currentUser;

				/* fix for issue #91 */
				if (this.dialogData.assetItem) {
					this.asset = this.dialogData.assetItem;
					this.getRules(this.asset);
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: combined route parameters", false);
			}
		});
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		if (!this.dialogData.isAttachedtoEvent) {
			this.attachedToEvent = false;
		}

		if (this.dialogData.editing) {
			this.editing = true;
			this.action = this.dialogData.action;
			if (this.action.TargetCompletionTimestamp != undefined) {
				this.dialogForm = this.fb.group({
					actionType: [this.action.ActionType, Validators.required],
					disableRule: [false],
					skipAction: [false],
					details: [this.action.Details, Validators.required],
					workOrderReference: [this.action.WorkOrderReference],
					assignedToUser: [this.action.AssignedToUserSub, Validators.required],
					targetCompletionTimestamp: [new Date(this.action.TargetCompletionTimestamp), Validators.required]
				});
			}
		} else {
			this.dialogForm = this.fb.group({
				actionType: [0, Validators.required],
				disableRule: [false],
				ruleList: [],
				skipAction: [false],
				details: ['', Validators.required],
				workOrderReference: [''],
				assignedToUser: ['', Validators.required],
				targetCompletionTimestamp: [, Validators.required]
			});
		}
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * skipAction
	 */
	skipAction() {

		this.globalFunctions.UserConfirm$("Are you sure you want to skip creating an action?").subscribe(async OKResult => {
			if (OKResult) {
				this.dialogForm.patchValue({
					skipAction: true
				});
				this.dialogRef.close(this.dialogForm.value);
			}
		});
	}

	/**
	 * getCommentLength
	 * @returns 
	 */
	getCommentLength(): number {
		return this.dialogForm.get('details')?.value.length || 0;
	}

	/**
	 * Get List of Account Users
	 * @param accountGUID
	 */
	getAccountUsers(accountGUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.accountUsers = []
		let params = {
			"systemAccount": accountGUID,
			"showDisabled": true,
			"IncludeSiteAdmins": true
		}
		// Get Users for the account
		this.accountsService.getAccountUsers$(this.selectedAccountUID, params).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((users) => !!users),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (users) => {
				try {
					this.accountUsers = <IAccountUser[]>users.filter((x: IAccountUser) => !x.AccountUserDisabled && !x.AdminSite);
					// if (!this.currentUser.AdminSite) {
					// 	this.accountUsers = this.accountUsers.filter((x: IAccountUser) => !x.AdminSite && !x.AccountUserDisabled);
					// }
				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {
				if (error.status != 404) {
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.getAccountUsers$: Error retrieving Invitations")
				} else {
					console.error(error.error);
				}
			},
			//complete: () => { console.log("this.accountsService.getAccountUsers$: Complete"); }
		});
	}

	/**
	 * selectDate
	 * @param event event  
	 */
	selectDate(event: any) {
		let timestamp: number = event.value.getTime();
		this.dialogForm.patchValue({
			targetCompletionTimestamp: timestamp
		});
	}

	/** getRules */
	getRules(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.rulesDataService.getRules$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((asset) => !!asset),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (rules: any) => {
				if (rules[0].RuleID != 0) {
					this.Rules = rules;
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRules$", false); },
			//complete: () => { console.log("this.siteSubscriptions.selectedOrganisationUID$.subscribe: Complete"); }
		});
	}

	/**
	 * onDialogClose
	 */
	onDialogClose() {
		if (this.dialogForm.valid && this.dialogForm.dirty) {
			// let selectedRules = this.Rules.filter((x: Rule) => this.dialogForm.value['ruleList'].includes(x.RuleID));
			// this.dialogForm.patchValue({
			// 	ruleList: JSON.stringify(selectedRules)
			// });
			// this.dialogForm.updateValueAndValidity();
			this.dialogRef.close(this.dialogForm.value);
		}
	}

	/**
	 * toggleAllRule
	 * @param event event  
	 */
	toggleAllRule(event: any) {
		if (event.checked) {
			let arr: number[] = [];
			this.Rules.forEach(rule => {
				if (rule.RuleID) {
					arr.push(rule.RuleID);
				}
			});
			this.dialogForm.patchValue({
				ruleList: arr
			});
		} else {
			this.dialogForm.patchValue({
				ruleList: []
			});
		}
		this.dialogForm.updateValueAndValidity();
	}

	/**
	 * multiSelectChange
	 * @param event event 
	 */
	multiSelectChange(event: any) {
		if (event.value.length == this.Rules.length) {
			this.allSelected = true;
		} else {
			this.allSelected = false;
		}
	}

	/**
	 * itemSelected
	 * @param event event  
	 */
	itemSelected(event: any) {
		this.numberSelected++;
		this.checkSelected();
	}

	/**
	 * itemRemoved
	 * @param event event  
	 */
	itemRemoved(event: any) {
		this.numberSelected--;
		this.checkSelected();
	}

	/**
	 * checkSelected
	 */
	checkSelected() {
		if (this.numberSelected == this.Rules.length) {
			this.allSelected = true;
		} else {
			this.allSelected = false;
		}
	}
}
